/**
 * Debug console output.
 *
 * Enabled by `context.debug`, which the server has already gated on the `debug` setting
 * *and* on the visitor being an administrator — see Frontend\Assets. The browser never
 * decides this for itself, the same way it never classifies the page for itself.
 *
 * `console.groupCollapsed` and `console.table` are missing or inert in some in-app
 * WebViews, which is precisely the traffic this bundle exists to survive, so every call
 * degrades to a flat `console.info` rather than throwing inside a collector.
 */

const PREFIX = '[trackdaddy]';

export function createLogger(enabled, win = typeof window !== 'undefined' ? window : {}) {
  const out = win.console || null;
  const on = Boolean(enabled) && out !== null;

  /**
   * The one place that touches the console. A method the console does not have falls
   * back to `log`, and a console without even that writes nothing — an exception raised
   * here would surface inside Tracker.track() and take the caller's collector down with
   * it, which would cost events, not just logging.
   */
  function call(method, args) {
    const fn = typeof out[method] === 'function' ? out[method] : out.log;

    if (typeof fn !== 'function') {
      return;
    }

    fn.apply(out, args);
  }

  function write(method, args) {
    if (!on) {
      return;
    }

    call(method, [PREFIX].concat(args));
  }

  return {
    // Lets a caller skip building a details object that would only be thrown away.
    enabled: on,

    info(...args) {
      write('info', args);
    },

    warn(...args) {
      write('warn', args);
    },

    /**
     * One collapsed group per event: the label carries the name, the body carries the
     * envelope. Flattens to a single line when the console has no grouping.
     *
     * @param {string} label
     * @param {Object} details
     */
    group(label, details) {
      if (!on) {
        return;
      }

      if (typeof out.groupCollapsed !== 'function' || typeof out.groupEnd !== 'function') {
        write('info', [label, details]);

        return;
      }

      out.groupCollapsed(PREFIX + ' ' + label);

      // No prefix inside the group; the label already carries it.
      Object.keys(details).forEach((key) => {
        call('log', [key, details[key]]);
      });

      out.groupEnd();
    },
  };
}
